import { useTranslations } from "next-intl";
import type { ProfileType } from "@/lib/types";
import { Input } from "@/components/ui";
import { CityPicker } from "@/components/CityPicker";
import { CountryPicker } from "@/components/CountryPicker";
import { SelectableCard } from "../SelectableCard";
import { ManIcon, WomanIcon, CoupleIcon } from "../icons";
import { LaptopIcon } from "@/components/shell/icons";
import type { FieldErrors, JoinDraft } from "../schema";

const SEEKING: { type: ProfileType; icon: React.ReactNode }[] = [
  { type: "male", icon: <ManIcon className="size-6" /> },
  { type: "female", icon: <WomanIcon className="size-6" /> },
  { type: "couple", icon: <CoupleIcon className="size-6" /> },
];

export function DetailsStep({
  draft,
  errors,
  onToggleSeeking,
  onField,
}: {
  draft: JoinDraft;
  errors: FieldErrors;
  onToggleSeeking: (type: ProfileType) => void;
  onField: (patch: Partial<JoinDraft>) => void;
}) {
  const t = useTranslations("join.details");
  const tJoin = useTranslations("join");
  const tTypes = useTranslations("common.profileTypes");
  return (
    <div>
      <h1 className="font-display text-2xl font-semibold text-white sm:text-3xl">
        {t("title")}
      </h1>
      <p className="mt-2 text-sm text-white/55">
        {t("subtitle")}
      </p>

      <div className="mt-5 grid grid-cols-3 gap-3">
        {SEEKING.map(({ type, icon }) => (
          <SelectableCard
            key={type}
            selected={draft.seeking.includes(type)}
            onSelect={() => onToggleSeeking(type)}
            title={tTypes(type)}
            icon={icon}
          />
        ))}
      </div>
      {errors.seeking && (
        <p className="mt-2 text-xs font-medium text-danger-light">
          {tJoin(errors.seeking)}
        </p>
      )}

      <div className="mt-8 flex flex-col gap-5">
        <h2 className="font-display text-lg font-semibold text-white">
          {t("accountSection")}
        </h2>
        <div className="grid gap-5 sm:grid-cols-2">
          <Input
            label={t("emailLabel")}
            type="email"
            autoComplete="email"
            required
            placeholder={t("emailPlaceholder")}
            value={draft.email}
            onChange={(e) => onField({ email: e.target.value })}
            error={errors.email ? tJoin(errors.email) : undefined}
          />
          <Input
            label={t("passwordLabel")}
            type="password"
            autoComplete="new-password"
            required
            placeholder={t("passwordPlaceholder")}
            value={draft.password}
            onChange={(e) => onField({ password: e.target.value })}
            error={errors.password ? tJoin(errors.password) : undefined}
          />
          <Input
            label={t("usernameLabel")}
            required
            autoCapitalize="none"
            spellCheck={false}
            placeholder={t("usernamePlaceholder")}
            value={draft.username}
            onChange={(e) =>
              onField({ username: e.target.value.toLowerCase().trim() })
            }
            error={errors.username ? tJoin(errors.username) : undefined}
            hint={t("usernameHint")}
          />
          {draft.virtualOnly ? (
            <CountryPicker
              containerClassName="sm:col-span-2"
              label={t("countryLabel")}
              value={draft.country}
              onChange={(country) => onField({ country })}
              error={errors.country ? tJoin(errors.country) : undefined}
              hint={t("countryHintVirtual")}
            />
          ) : (
            <CityPicker
              containerClassName="sm:col-span-2"
              label={t("cityLabel")}
              value={draft.city}
              // Picking a suggestion autofills the country; free text clears
              // it again — there's no visible country field here, so a stale
              // autofill must not survive an edit (validation then demands a
              // fresh pick from the list).
              onChange={(city, cityId, countryCode) =>
                onField({ city, cityId, country: countryCode ?? "" })
              }
              error={errors.city ? tJoin(errors.city) : undefined}
              hint={
                draft.city && draft.cityId == null ? t("cityHintPick") : undefined
              }
            />
          )}
          <label className="flex cursor-pointer items-start gap-2.5 text-sm text-white/75 sm:col-span-2">
            <input
              type="checkbox"
              checked={draft.virtualOnly}
              onChange={(e) => {
                const on = e.target.checked;
                onField(
                  on
                    ? { virtualOnly: true, city: "", cityId: null }
                    : { virtualOnly: false },
                );
              }}
              className="mt-0.5 size-4 shrink-0 accent-flamingo"
            />
            <span>
              <LaptopIcon className="mr-1 inline-block size-3.5 align-[-0.125em]" />
              {t("virtualLabel")}
              <span className="mt-0.5 block text-xs text-white/55">
                {t("virtualHint")}
              </span>
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}
